import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { TurnstileLoginEnabled } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';

/** Options passed to `window.turnstile.render`. */
interface ITurnstileRenderOptions {
  sitekey: string;
  size?: string;
  appearance?: string;
  theme?: string;
  callback: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
}

interface ITurnstile {
  render: (container: HTMLElement, options: ITurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    turnstile?: ITurnstile;
    Cypress?: unknown;
  }
}

export interface IUseTurnstileTokenResult {
  /** The Turnstile token once captured; undefined until the user completes the widget. */
  token: string | undefined;
  /** The <div> that must be rendered in the consumer's JSX for the widget to mount. */
  widget: ReactNode;
  /** Resets the widget and clears the token (call after a failed login attempt). */
  reset: () => void;
  /**
   * Whether a real, user-supplied token is required before submit. True only
   * when enforcement is on AND not bypassed (Cypress). Consumers gate the
   * submit CTA on `enabled && !token` so a tokenless login can't be sent while
   * the widget is still resolving.
   */
  enabled: boolean;
}

const noop = (): void => {};

/**
 * Cloudflare Turnstile integration. Gated by FF + bypassed under Cypress.
 * Bypassing under Cypress is required because Cloudflare's test sitekey
 * (`1x00000000000000000000AA`) is rate-limited per IP/UA, and a full E2E
 * suite makes hundreds of widget mounts in minutes. When bypassed, the
 * Cypress test layer (`cy.bypassTurnstileForCypress()`) injects the
 * dev-backend-accepted dummy token at the network boundary so login
 * still works end-to-end without ever hitting Cloudflare.
 */
export const useTurnstileToken = (): IUseTurnstileTokenResult => {
  // Default to enabled so a not-yet-resolved PostHog flag (cold load / slow
  // network) can't drop enforcement and submit a tokenless login. Rollout is
  // 100%, so the resolved value is true anyway. GOTU-4317.
  const turnstileEnabled = useFeatureFlag(TurnstileLoginEnabled, true);
  const isCypress = typeof window !== 'undefined' && Boolean(window.Cypress);

  const active = turnstileEnabled && !isCypress;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

    if (turnstileEnabled && !siteKey) {
      console.error('[Turnstile] ERROR: VITE_TURNSTILE_SITE_KEY environment variable is not set!');
    }

    if (!turnstileEnabled || !siteKey || window.Cypress) {
      return undefined;
    }

    const initTurnstile = (): void => {
      if (window.turnstile && containerRef.current && !widgetIdRef.current) {
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            size: 'flexible',
            appearance: 'always',
            theme: 'light',
            callback: (nextToken: string) => {
              setToken(nextToken);
            },
            'error-callback': () => {
              setToken(undefined);
            },
            'expired-callback': () => {
              setToken(undefined);
            },
          });
        } catch {
          // Swallow — matches legacy behavior (sets local error flag there; here we simply
          // leave the token undefined so the consumer can decide how to react).
        }
      }
    };

    let checkInterval: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    // Check if Turnstile is already loaded.
    if (window.turnstile) {
      initTurnstile();
    } else {
      // Wait for Turnstile to load with a timeout.
      checkInterval = setInterval(() => {
        if (window.turnstile) {
          if (checkInterval) clearInterval(checkInterval);
          if (timeoutId) clearTimeout(timeoutId);
          initTurnstile();
        }
      }, 100);

      // Timeout after 10 seconds — silently give up so the form remains usable.
      timeoutId = setTimeout(() => {
        if (checkInterval) clearInterval(checkInterval);
      }, 10000);
    }

    // Cleanup on unmount. Remove the widget from Turnstile's internal
    // registry so a subsequent mount (e.g., navigating back to /login from
    // /signup via the header) re-renders it fresh instead of silently
    // failing to draw.
    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (timeoutId) clearTimeout(timeoutId);
      if (window.turnstile && widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Swallow — widget may have already been removed by the SDK.
        }
        widgetIdRef.current = null;
      }
    };
  }, [turnstileEnabled]);

  const reset = useCallback(() => {
    if (window.turnstile && widgetIdRef.current) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (error) {
        console.error('Failed to reset Turnstile:', error);
      }
    }
    setToken(undefined);
  }, []);

  if (!turnstileEnabled) {
    return { token: undefined, widget: null, reset: noop, enabled: false };
  }

  // Under Cypress we skip the actual Cloudflare init (rate-limit reasons —
  // see the comment above the hook), but we still render the container so
  // E2E specs can assert the widget mount-point is present after auth
  // navigation events (logout → /login, token-expiration redirect, etc.).
  // The dummy turnstileToken is injected at the network boundary by
  // `cy.bypassTurnstileForCypress()`.
  if (!active) {
    return {
      token: undefined,
      widget: <div data-cy="turnstile-widget" />,
      reset: noop,
      enabled: false,
    };
  }

  return {
    token,
    widget: <div ref={containerRef} data-cy="turnstile-widget" />,
    reset,
    enabled: true,
  };
};
